import AiLpFormPage from "./AiLpFormPage";

// ホワイトペーパー配布ページの共通の作り。
//
// 資料ごとに1ページ・1URL・1フォームにする。同じページで出し分けないのは、
// どの資料を請求したリードなのかを、通知でも一覧でも区別するため。
// 資料そのものの中身（紙面・扱う項目・ページ数）だけを各ページから渡す。
//
// 配布は完了画面で直接渡す。HubSpotのフォローアップメールは
// 有料プランの機能が要るうえ、メールを待たせるぶん離脱する。
// リードはフォーム送信の時点で記録される。
//
// 新しい資料を足すときにやること:
//   1. public/downloads/ にPDF、public/images/whitepaper/<資料名>/ に紙面画像
//   2. このコンポーネントを使うページを作り、App.js のルートと BARE_ROUTES に足す
//   3. src/seo/siteMeta.js にタイトルと説明文
//   4. wp-theme/inc/lead.php に自動返信の文面（leadKind と同じキー）
//   5. HubSpotでフォームを作り、GUIDを src/api/hubspot.js に

const PRIMARY = "#2B7FFF";
const INK = "#1B2330";
const round = "'M PLUS Rounded 1c', sans-serif";

function Slide({ src, alt, w }) {
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      width={720}
      height={405}
      style={{
        width: w,
        height: "auto",
        display: "block",
        borderRadius: 8,
        border: "1px solid #E3E9F2",
        boxShadow: "0 6px 20px rgba(27,35,48,0.10)",
      }}
    />
  );
}

// 紙面。スライドなので横長。A4縦の資料と違って扇状に重ねると潰れるため、
// 表紙を大きく出して、中身を3枚並べる形にしている。
function Intro({ cover, inside, spec, topics }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <div style={{ marginBottom: 12 }}>
        <Slide src={cover} alt="資料の表紙" w="100%" />
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        {inside.map((s) => (
          <div key={s.src} style={{ flex: 1, minWidth: 0 }}>
            <Slide src={s.src} alt="" w="100%" />
            <p
              style={{
                fontFamily: round,
                fontSize: 10.5,
                fontWeight: 700,
                color: "#8A94A6",
                margin: "5px 0 0",
                textAlign: "center",
              }}
            >
              {s.cap}
            </p>
          </div>
        ))}
      </div>

      <p
        style={{
          fontFamily: round,
          fontSize: 12,
          fontWeight: 700,
          color: PRIMARY,
          margin: "14px 0 0",
          textAlign: "center",
        }}
      >
        {spec}
      </p>

      <div
        style={{
          background: "#F8FAFD",
          border: "1px solid #EDF1F7",
          borderRadius: 16,
          padding: "18px 20px",
          marginTop: 18,
        }}
      >
        <p
          style={{
            fontFamily: round,
            fontSize: 13,
            fontWeight: 800,
            color: INK,
            margin: "0 0 10px",
          }}
        >
          扱っていること
        </p>
        <ul
          style={{
            margin: 0,
            paddingLeft: "1.15em",
            display: "flex",
            flexDirection: "column",
            gap: 7,
          }}
        >
          {topics.map((t) => (
            <li
              key={t}
              style={{ fontSize: 13, color: "#5B6472", lineHeight: 1.75 }}
            >
              {t}
            </li>
          ))}
        </ul>
      </div>

      <p
        style={{
          fontSize: 12.5,
          color: "#8A94A6",
          margin: "18px 0 0",
          textAlign: "center",
          lineHeight: 1.8,
        }}
      >
        営業のご連絡が不要な場合は、備考欄にその旨をご記入ください。
      </p>
    </div>
  );
}

function DoneExtra({ title, spec, pdfHref }) {
  return (
    <div style={{ marginTop: 26, textAlign: "left" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 14,
          flexWrap: "wrap",
          background: "#F8FAFD",
          border: "1px solid #EDF1F7",
          borderRadius: 14,
          padding: "14px 16px",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              fontFamily: round,
              fontSize: 14.5,
              fontWeight: 800,
              color: INK,
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            {title}
          </p>
          <p style={{ fontSize: 11.5, color: "#8A94A6", margin: "3px 0 0" }}>
            {spec}
          </p>
        </div>
        <a
          href={pdfHref}
          download
          style={{
            fontFamily: round,
            fontSize: 13,
            fontWeight: 700,
            color: "#fff",
            background: PRIMARY,
            padding: "8px 16px",
            borderRadius: 10,
            textDecoration: "none",
            whiteSpace: "nowrap",
            flex: "none",
          }}
        >
          PDF
        </a>
      </div>

      <p
        style={{
          fontSize: 12.5,
          color: "#8A94A6",
          margin: "18px 0 0",
          lineHeight: 1.9,
          textAlign: "center",
        }}
      >
        このページを閉じたあとで必要になった場合は、
        <br />
        <a href="mailto:info@leaps-inc.com" style={{ color: PRIMARY }}>
          info@leaps-inc.com
        </a>{" "}
        までご連絡ください。あらためてお送りします。
      </p>
    </div>
  );
}

// formId が空なら共通フォームに送られる（リードは取れるが、
// 通知の件名で資料を区別できない）。専用フォームを作ったらGUIDを渡すこと。
function WhitepaperPage({
  formId,
  leadKind,
  title,
  lead,
  pdfHref,
  spec,
  cover,
  inside,
  topics,
}) {
  return (
    <AiLpFormPage
      formId={formId || undefined}
      leadKind={leadKind}
      eyebrow="無料ダウンロード"
      title={title}
      lead={lead}
      intro={
        <Intro cover={cover} inside={inside} spec={spec} topics={topics} />
      }
      fields={[
        { name: "company", label: "会社名", type: "text", required: true },
        { name: "name", label: "お名前", type: "text", required: true },
        {
          name: "email",
          label: "メールアドレス",
          type: "email",
          required: true,
        },
        { name: "tel", label: "電話番号", type: "tel", required: true },
        { name: "message", label: "備考（任意）", type: "textarea" },
      ]}
      footnote="ご入力の情報は資料のご案内以外には使用しません"
      submitLabel="ダウンロードする"
      doneTitle="ダウンロードいただけます"
      doneMsg="ありがとうございます。下記からご利用ください。"
      doneExtra={<DoneExtra title={title} spec={spec} pdfHref={pdfHref} />}
    />
  );
}

export default WhitepaperPage;
